import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Refresh, Search } from '@mui/icons-material';
import getSensitiveTools from 'redux/actions/Common/getSensitiveTools';
import updateSensitiveTools from 'redux/actions/Common/updateSensitiveTools';
import deleteSensitiveTool from 'redux/actions/Common/deleteSensitiveTool';
import getOrganizationById from 'redux/actions/Common/getOrganizationById';
import updateOrganization from 'redux/actions/Common/updateOrganization';

// The admin panel only exposes the edit controls to ROLE_ADMIN — the API rejects
// (or silently drops) the write for anyone else. When the logged-in user record
// carries no role at all we stay permissive and let the API be the gate.
const canEditSensitiveTools = () => {
  try {
    const role = JSON.parse(localStorage.getItem('userInfo') || '{}')?.role;
    return role ? role === 'ROLE_ADMIN' : true;
  } catch (e) {
    return true;
  }
};

const McpToolsConfig = ({ org }) => {
  const dispatch = useDispatch();

  const orgId = org?.id;
  const canEdit = useMemo(() => canEditSensitiveTools(), []);

  const [currentTab, setCurrentTab] = useState('orgAccess');

  // Server truth
  const [globalTools, setGlobalTools] = useState({});
  const [orgAccess, setOrgAccess] = useState({});

  // Local, unsaved edits
  const [draftGlobalTools, setDraftGlobalTools] = useState({});
  const [draftOrgAccess, setDraftOrgAccess] = useState({});

  const [fetchingTools, setFetchingTools] = useState(false);
  const [fetchingOrg, setFetchingOrg] = useState(false);
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [savingOrg, setSavingOrg] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const [newToolName, setNewToolName] = useState('');
  const [search, setSearch] = useState('');
  const [toolToDelete, setToolToDelete] = useState(null);
  const [alert, setAlert] = useState(null);

  const showAlert = (message, severity = 'info') =>
    setAlert({ message, severity });

  const fetchGlobalTools = useCallback(() => {
    setFetchingTools(true);
    dispatch(
      getSensitiveTools(
        (data) => {
          const tools = data || {};
          setGlobalTools(tools);
          setDraftGlobalTools(tools);
          setFetchingTools(false);
        },
        (message) => {
          setFetchingTools(false);
          showAlert(message, 'error');
        },
      ),
    );
  }, [dispatch]);

  const fetchOrgAccess = useCallback(() => {
    if (!orgId) return;
    setFetchingOrg(true);
    dispatch(
      getOrganizationById(
        orgId,
        { fetchUsers: false, fetchTeams: false },
        (data) => {
          const access = data?.sensitiveToolAccess || {};
          setOrgAccess(access);
          setDraftOrgAccess(access);
          setFetchingOrg(false);
        },
        (message) => {
          setFetchingOrg(false);
          showAlert(message, 'error');
        },
      ),
    );
  }, [dispatch, orgId]);

  useEffect(() => {
    fetchGlobalTools();
  }, [fetchGlobalTools]);

  useEffect(() => {
    setOrgAccess({});
    setDraftOrgAccess({});
    fetchOrgAccess();
  }, [fetchOrgAccess]);

  const allToolNames = useMemo(
    () =>
      Object.keys(draftGlobalTools || {}).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' }),
      ),
    [draftGlobalTools],
  );

  // Only tools flagged sensitive globally are gated per organization — anything
  // else is callable by every org and has nothing to toggle here.
  const sensitiveToolNames = useMemo(
    () => allToolNames.filter((name) => draftGlobalTools?.[name] === true),
    [allToolNames, draftGlobalTools],
  );

  const nonSensitiveToolNames = useMemo(
    () => allToolNames.filter((name) => draftGlobalTools?.[name] !== true),
    [allToolNames, draftGlobalTools],
  );

  // Entries the org still carries for tools that are no longer on the global
  // sensitive list — harmless, but worth surfacing so they can be cleaned up.
  const staleOrgEntries = useMemo(
    () =>
      Object.keys(orgAccess || {})
        .filter((name) => globalTools?.[name] !== true)
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
    [orgAccess, globalTools],
  );

  const matchesSearch = useCallback(
    (name) =>
      !search?.trim() ||
      name?.toLowerCase()?.includes(search?.trim()?.toLowerCase()),
    [search],
  );

  const changedGlobalTools = useMemo(() => {
    const diff = {};
    Object.keys(draftGlobalTools || {}).forEach((name) => {
      if (
        Boolean(draftGlobalTools?.[name]) !== Boolean(globalTools?.[name]) ||
        !(name in (globalTools || {}))
      ) {
        diff[name] = Boolean(draftGlobalTools?.[name]);
      }
    });
    return diff;
  }, [draftGlobalTools, globalTools]);

  const changedOrgAccess = useMemo(() => {
    const diff = {};
    Object.keys(draftOrgAccess || {}).forEach((name) => {
      if (Boolean(draftOrgAccess?.[name]) !== Boolean(orgAccess?.[name])) {
        diff[name] = Boolean(draftOrgAccess?.[name]);
      }
    });
    return diff;
  }, [draftOrgAccess, orgAccess]);

  const globalDirtyCount = Object.keys(changedGlobalTools).length;
  const orgDirtyCount = Object.keys(changedOrgAccess).length;

  const allowedCount = sensitiveToolNames.filter(
    (name) => draftOrgAccess?.[name] === true,
  ).length;

  const handleAddTool = () => {
    const name = newToolName?.trim();
    if (!name) return;
    if (name in (draftGlobalTools || {})) {
      showAlert(`"${name}" is already on the list`, 'warning');
      return;
    }
    setDraftGlobalTools((prev) => ({ ...prev, [name]: true }));
    setNewToolName('');
    showAlert(`"${name}" staged — hit Save changes to persist it`, 'info');
  };

  const handleSaveGlobalTools = () => {
    if (!globalDirtyCount) return;
    setSavingGlobal(true);
    dispatch(
      updateSensitiveTools(
        changedGlobalTools,
        () => {
          setSavingGlobal(false);
          showAlert('Sensitive tool list updated', 'success');
          fetchGlobalTools();
        },
        (message) => {
          setSavingGlobal(false);
          showAlert(message, 'error');
        },
      ),
    );
  };

  const handleDeleteTool = () => {
    const name = toolToDelete;
    if (!name) return;
    // A tool that was only staged locally has nothing to delete server-side.
    if (!(name in (globalTools || {}))) {
      setDraftGlobalTools((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
      setToolToDelete(null);
      return;
    }
    setDeleting(name);
    dispatch(
      deleteSensitiveTool(
        name,
        () => {
          setDeleting(null);
          setToolToDelete(null);
          showAlert(`Removed "${name}" from the list`, 'success');
          fetchGlobalTools();
        },
        (message) => {
          setDeleting(null);
          setToolToDelete(null);
          showAlert(message, 'error');
        },
      ),
    );
  };

  const handleSaveOrgAccess = () => {
    if (!orgId || !orgDirtyCount) return;
    setSavingOrg(true);
    dispatch(
      updateOrganization(
        orgId,
        { sensitiveToolAccess: changedOrgAccess },
        () => {
          setSavingOrg(false);
          showAlert(`Access updated for ${org?.organizationName}`, 'success');
          fetchOrgAccess();
        },
        (message) => {
          setSavingOrg(false);
          showAlert(message, 'error');
        },
      ),
    );
  };

  const setAllOrgAccess = (value) => {
    setDraftOrgAccess((prev) => {
      const next = { ...prev };
      sensitiveToolNames.forEach((name) => {
        next[name] = value;
      });
      return next;
    });
  };

  const loading = fetchingTools || fetchingOrg;

  if (!orgId) {
    return (
      <Box padding={2}>
        <Typography variant="body2" color={'text.secondary'}>
          Select an organization to configure its MCP tool access.
        </Typography>
      </Box>
    );
  }

  return (
    <Box padding={1} display={'flex'} flexDirection={'column'} gap={1}>
      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        gap={1}
        flexWrap={'wrap'}
      >
        <Box>
          <Typography variant="body1" fontWeight={'bold'}>
            MCP Tools Configurations
          </Typography>
          <Typography variant="caption" color={'text.secondary'}>
            {org?.organizationName}
          </Typography>
        </Box>
        <Box display={'flex'} alignItems={'center'} gap={1}>
          <TextField
            placeholder="Search tool"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Reload from server" arrow>
            <span>
              <IconButton
                size="small"
                disabled={loading}
                onClick={() => {
                  fetchGlobalTools();
                  fetchOrgAccess();
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {!canEdit && (
        <Alert severity="info">
          You are signed in without the ROLE_ADMIN role — this page is read
          only.
        </Alert>
      )}

      <Box border={'1px solid #d3d3d3'}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          aria-label="MCP tools tabs"
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab
            value={'orgAccess'}
            label={
              <Typography variant="caption" fontWeight={'bold'}>
                Org Tool Access
              </Typography>
            }
          />
          <Tab
            value={'globalTools'}
            label={
              <Typography variant="caption" fontWeight={'bold'}>
                Sensitive Tools List
              </Typography>
            }
          />
        </Tabs>
      </Box>

      {loading ? (
        <Box
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
          gap={1}
          padding={4}
        >
          <CircularProgress size={16} />
          <Typography variant="body2">
            Loading tool configuration ...
          </Typography>
        </Box>
      ) : currentTab === 'orgAccess' ? (
        <Box display={'flex'} flexDirection={'column'} gap={1}>
          <Alert severity="info">
            Only tools flagged sensitive on the global list are gated. A
            sensitive tool is usable by this org only when it is switched on
            below — no entry means denied, there is no default-on.
          </Alert>

          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'space-between'}
            gap={1}
            flexWrap={'wrap'}
          >
            <Box display={'flex'} alignItems={'center'} gap={1}>
              <Chip
                size="small"
                label={`${allowedCount} of ${sensitiveToolNames.length} allowed`}
                color={allowedCount ? 'primary' : 'default'}
              />
              {orgDirtyCount ? (
                <Chip
                  size="small"
                  color="warning"
                  label={`${orgDirtyCount} unsaved change${
                    orgDirtyCount > 1 ? 's' : ''
                  }`}
                />
              ) : null}
            </Box>
            <Box display={'flex'} alignItems={'center'} gap={1}>
              <Button
                size="small"
                variant="outlined"
                disabled={!canEdit || !sensitiveToolNames.length || savingOrg}
                onClick={() => setAllOrgAccess(true)}
              >
                Allow all
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={!canEdit || !sensitiveToolNames.length || savingOrg}
                onClick={() => setAllOrgAccess(false)}
              >
                Deny all
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={!orgDirtyCount || savingOrg}
                onClick={() => setDraftOrgAccess(orgAccess)}
              >
                Discard
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={!canEdit || !orgDirtyCount || savingOrg}
                startIcon={
                  savingOrg ? <CircularProgress size={14} /> : undefined
                }
                onClick={handleSaveOrgAccess}
              >
                Save changes
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="caption" fontWeight={'bold'}>
                      Sensitive Tool Name
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="caption" fontWeight={'bold'}>
                      Current
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="caption" fontWeight={'bold'}>
                      Access for this org
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sensitiveToolNames.filter(matchesSearch).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" color={'text.secondary'}>
                        {sensitiveToolNames.length === 0
                          ? 'No tool is flagged sensitive yet — every org can call every MCP tool. Add one on the Sensitive Tools List tab.'
                          : 'No tool matches your search.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sensitiveToolNames.filter(matchesSearch).map((name) => {
                    const allowed = draftOrgAccess?.[name] === true;
                    const changed = name in changedOrgAccess;
                    return (
                      <TableRow key={name} hover>
                        <TableCell>
                          <Box
                            display={'flex'}
                            alignItems={'center'}
                            gap={1}
                            flexWrap={'wrap'}
                          >
                            <Typography variant="body2" fontWeight={'bold'}>
                              {name}
                            </Typography>
                            {changed ? (
                              <Chip
                                size="small"
                                color="warning"
                                label="unsaved"
                              />
                            ) : null}
                            {!(name in (orgAccess || {})) ? (
                              <Tooltip
                                arrow
                                title="This org has no entry for the tool, which the backend reads as denied"
                              >
                                <Chip size="small" label="no entry" />
                              </Tooltip>
                            ) : null}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            variant="outlined"
                            color={
                              orgAccess?.[name] === true ? 'success' : 'default'
                            }
                            label={
                              orgAccess?.[name] === true ? 'Allowed' : 'Denied'
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            size="small"
                            checked={allowed}
                            disabled={!canEdit || savingOrg}
                            onChange={(e) =>
                              setDraftOrgAccess((prev) => ({
                                ...prev,
                                [name]: e.target.checked,
                              }))
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {nonSensitiveToolNames.length ? (
            <Box>
              <Typography variant="caption" color={'text.secondary'}>
                Not sensitive — available to every organization, nothing to
                configure:
              </Typography>
              <Box display={'flex'} gap={0.5} flexWrap={'wrap'} marginTop={0.5}>
                {nonSensitiveToolNames.map((name) => (
                  <Chip
                    key={name}
                    size="small"
                    variant="outlined"
                    label={name}
                  />
                ))}
              </Box>
            </Box>
          ) : null}

          {staleOrgEntries.length ? (
            <Alert severity="warning">
              <Typography variant="body2" fontWeight={'bold'}>
                Stale entries on this org
              </Typography>
              <Typography variant="caption">
                These tools are stored on the org but are not on the global
                sensitive list, so they have no effect:{' '}
                {staleOrgEntries.join(', ')}
              </Typography>
            </Alert>
          ) : null}
        </Box>
      ) : (
        <Box display={'flex'} flexDirection={'column'} gap={1}>
          <Alert severity="warning">
            This list is global, shared by every organization. Flagging a tool
            sensitive takes effect immediately — every org without an explicit
            grant loses access to it at once, so grant access to the orgs
            already using it before flipping the flag.
          </Alert>

          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'space-between'}
            gap={1}
            flexWrap={'wrap'}
          >
            <Box display={'flex'} alignItems={'center'} gap={1}>
              <TextField
                placeholder="Tool name e.g. create_deal_for_me"
                variant="outlined"
                size="small"
                value={newToolName}
                disabled={!canEdit}
                onChange={(e) => setNewToolName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTool();
                  }
                }}
                sx={{ minWidth: 280 }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<Add fontSize="small" />}
                disabled={!canEdit || !newToolName?.trim()}
                onClick={handleAddTool}
              >
                Add tool
              </Button>
            </Box>
            <Box display={'flex'} alignItems={'center'} gap={1}>
              {globalDirtyCount ? (
                <Chip
                  size="small"
                  color="warning"
                  label={`${globalDirtyCount} unsaved change${
                    globalDirtyCount > 1 ? 's' : ''
                  }`}
                />
              ) : null}
              <Button
                size="small"
                variant="outlined"
                disabled={!globalDirtyCount || savingGlobal}
                onClick={() => setDraftGlobalTools(globalTools)}
              >
                Discard
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={!canEdit || !globalDirtyCount || savingGlobal}
                startIcon={
                  savingGlobal ? <CircularProgress size={14} /> : undefined
                }
                onClick={handleSaveGlobalTools}
              >
                Save changes
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="caption" fontWeight={'bold'}>
                      Tool Name
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="caption" fontWeight={'bold'}>
                      Sensitive
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="caption" fontWeight={'bold'}>
                      This org
                    </Typography>
                  </TableCell>
                  {/* <TableCell align="right">
                    <Typography variant="caption" fontWeight={'bold'}>
                      Remove
                    </Typography>
                  </TableCell> */}
                </TableRow>
              </TableHead>
              <TableBody>
                {allToolNames.filter(matchesSearch).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color={'text.secondary'}>
                        {allToolNames.length === 0
                          ? 'The list is empty. Add a tool name to start gating it.'
                          : 'No tool matches your search.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  allToolNames.filter(matchesSearch).map((name) => {
                    const isSensitive = draftGlobalTools?.[name] === true;
                    const isNew = !(name in (globalTools || {}));
                    const changed = name in changedGlobalTools;
                    return (
                      <TableRow key={name} hover>
                        <TableCell>
                          <Box
                            display={'flex'}
                            alignItems={'center'}
                            gap={1}
                            flexWrap={'wrap'}
                          >
                            <Typography variant="body2" fontWeight={'bold'}>
                              {name}
                            </Typography>
                            {isNew ? (
                              <Chip size="small" color="info" label="new" />
                            ) : changed ? (
                              <Chip
                                size="small"
                                color="warning"
                                label="unsaved"
                              />
                            ) : null}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            size="small"
                            checked={isSensitive}
                            disabled={!canEdit || savingGlobal}
                            onChange={(e) =>
                              setDraftGlobalTools((prev) => ({
                                ...prev,
                                [name]: e.target.checked,
                              }))
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          {isSensitive ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              color={
                                orgAccess?.[name] === true
                                  ? 'success'
                                  : 'default'
                              }
                              label={
                                orgAccess?.[name] === true
                                  ? 'Allowed'
                                  : 'Denied'
                              }
                            />
                          ) : (
                            <Typography
                              variant="caption"
                              color={'text.secondary'}
                            >
                              Open to all
                            </Typography>
                          )}
                        </TableCell>
                        {/* <TableCell align="right">
                          <Tooltip
                            arrow
                            title="Remove the tool name from the list entirely"
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={!canEdit || deleting === name}
                                onClick={() => setToolToDelete(name)}
                              >
                                {deleting === name ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <Delete fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell> */}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Dialog
        open={Boolean(toolToDelete)}
        onClose={() => (deleting ? null : setToolToDelete(null))}
      >
        <DialogTitle>Remove "{toolToDelete}"?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This deletes the row from the global list, which is different from
            switching it off — with no row at all, the tool stops being gated
            and every org can call it again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            disabled={Boolean(deleting)}
            onClick={() => setToolToDelete(null)}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            disabled={Boolean(deleting)}
            startIcon={deleting ? <CircularProgress size={14} /> : undefined}
            onClick={handleDeleteTool}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(alert)}
        autoHideDuration={4000}
        onClose={() => setAlert(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setAlert(null)}
          severity={alert?.severity || 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {alert?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default McpToolsConfig;
